import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useCallback,
} from 'react'
import { useQuery, queryCache, useMutation } from 'react-query'
import { useLocation, globalHistory, useNavigate } from '@reach/router'
import { navigate } from 'gatsby'
import styled from 'styled-components'
import { Helmet } from 'react-helmet'

import { AppContext } from '../../containers/App'
import Button from '../../components/Button'
import Heading from '../../components/Heading'
import Layout from '../../components/Layout'
import Table from '../../components/Table'
import { PlaceHolder } from '../../utils/withLoading'
import AddUser from '../../containers/Users/AddUser'
import EditUser from '../../containers/Users/EditUser'
import UnitForm from './Form'
import AlertModal from './Modal'
import { isEmptyObject } from '../../utils/isEmptyObject'
import { useWithAuth, useLogout } from '../../containers/App'
import { useNotifications } from '../../containers/Notifications'
import { ID } from '../../types/id'
import withPermissions from '../../containers/withPermissions'
import { permissions as p, roles as rolesConstant } from '../../utils/constants'
import colors from '../../styles/colors'
import type {
  Units,
  User,
  Users,
  UnitsListProps,
  ModalStruct,
  RSPO,
} from './utils'
// @ts-ignore @todo add apprioprate declaration file
import editIcon from '../../images/editIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import deleteIcon from '../../images/deleteIcon.svg'
// @ts-ignore @todo add apprioprate declaration file
import closeIconRed from '../../images/closeIcon-red.svg'
// @ts-ignore @todo add apprioprate declaration file
import userIcon from '../../images/usersIcon.svg'

type Props = {
  location: {
    state: {
      unit: {
        id: number
        rspo: string
        name: string
        voivodeship: string
        city: string
        postcode: string
        address: string
      }
    }
  }
  hasPermissions: (permission: string) => boolean
  isHigherLevel: (level: Array<number>) => boolean
}

const decorate = withPermissions

function EditUnit({ isHigherLevel, hasPermissions, location }: Props) {
  const user = useContext<User>(AppContext)
  const handleModalStates = (value) => {
    value === 'AddUser' && setAddUserModalReady(false)
    value === 'EditUser' && setEditUserModalReady({})
  }
  const [users, setUsers] = useState<Users[]>()
  const [ready, setReady] = useState<boolean>(false)
  const [unitReady, setUnitReady] = useState<boolean>(true)
  const [importedUnit, setImportedUnit] = useState<UnitsListProps>({
    address: '',
    city: '',
    creationDate: null,
    id: null,
    name: '',
    postcode: '',
    publicUnit: null,
    rspo: -1,
    voivodeship: '',
  })
  const [alertModal, setAlertModal] = useState<ModalStruct>({
    isOpen: null,
    isLogout: false,
    redirect: '',
  })
  const [addUserModalReady, setAddUserModalReady] = useState<boolean>(false)
  const [editUserModalReady, setEditUserModalReady] = useState({})
  const normalisedUnitId = location.state.unit
    ? location.state.unit.id
    : user.unitId
  const [usersState, setUsersState] = useState({
    sorts: {},
    meta: {
      limit: 10,
      page: 0,
      total: 0,
    },
    filters: { search: '', unitId: normalisedUnitId },
  })
  const withAuth = useWithAuth()
  const logout = useLogout()
  const endpoint = `/diagnostic/rest/unit/${normalisedUnitId}`
  const endpointSearch = '/diagnostic/rest/rspo'
  const { notify } = useNotifications()

  const { data: currentUnit } = useQuery<UnitsListProps, string>(
    `unit-${normalisedUnitId}`,
    () =>
      withAuth(
        `/diagnostic/rest/unit/${normalisedUnitId}`,
        {
          method: 'GET',
        },
        true
      ),
    {
      retry: false,
      refetchOnWindowFocus: false,
    }
  )

  const fetchUsers = async () => {
    setReady(false)
    const result = await withAuth(`/user/rest/user/search`, {
      method: 'POST',
      payload: {
        filters: {
          unitId: normalisedUnitId,
        },
        meta: { ...usersState.meta },
      },
    })
    setUsersState({
      ...usersState,
      meta: { ...usersState.meta, total: result.meta.total },
    })
    return result
  }
  const fetchData = async () => {
    try {
      const result = await fetchUsers()
      setUsers(result.models)
      setReady(true)
    } catch {
      notify('Nie udało się pobrać użytkowników', 'error')
    }
  }
  useEffect(() => {
    fetchData()
  }, [addUserModalReady, editUserModalReady, usersState.meta.limit])

  const [handleDelete] = useMutation<any, ID>(
    (userId) =>
      withAuth(`/user/rest/user/${userId}`, {
        method: 'DELETE',
      })
        .then((res) => {
          if ('fieldErrors' in res) {
            // @todo notifications
          } else {
            fetchData()
            notify('Dodano zlecenie usunięcia użytkownika', 'success')
          }
        })
        .catch((e) => {
          e.status === 428 &&
            notify(
              'Brak możliwości usunięcia użytkownika z jednostki jeśli są do niego przypięte osoby diagnozowane',
              'error'
            )
        }),

    {}
  )
  const [mutate] = useMutation<any, RSPO>(
    (values) =>
      withAuth(endpoint, {
        method: 'PUT',
        payload: values,
      })
        .then((res) => {
          if ('fieldErrors' in res) {
            // @todo notifications
          } else {
            setUnitReady(true)
            queryCache.refetchQueries(`unit-${normalisedUnitId}`)
            notify('Edytowano jednostkę', 'success')
            // setAfterSubmit(false)
            // onClose()
          }
        })
        .catch((e) => {
          e.status === 409 &&
            notify('Taki identyfikator RSPO już istnieje', 'error')
          setUnitReady(true)
        }),

    {}
  )
  const searchUnits = async (value) => {
    setUnitReady(false)
    const result = await withAuth(`${endpointSearch}/${value}`, {
      method: 'GET',
    })
    return result
  }

  const handleSubmit = async (values: Units) => {
    try {
      const result = await searchUnits(values.rspo)
      if (result?.status !== 400) {
        // for some reason you cannot catch this error, comes back as results body
        if (result.id !== null) {
          notify('Taki identyfikator RSPO już istnieje', 'error')
        } else {
          setImportedUnit(result)
          setUnitReady(true)
          !(
            (
              Object.entries(result).toString() ===
              Object.entries(currentUnit).toString()
            ) //at this point its always defined.
          ) && setAlertModal({ ...alertModal, isOpen: false })
        }
      } else {
        notify('RSPO nie jest liczbą całkowitą nieujemną', 'error')
      }
    } catch (e) {
      switch (e.status) {
        case 404: {
          notify('Nie odnaleziono takiego id w RSPO', 'error')
          break
        }
        case 421: {
          notify('Brak poprawnej odpowiedzi z RSPO', 'error')
          break
        }
        default: {
          notify('Nie udało się pobrać danych jednostki', 'error')
        }
      }
    }
    setUnitReady(true)
  }

  const handleSave = () => {
    setUnitReady(false)
    const normalizedValues = {
      rspo: importedUnit.rspo,
    }
    mutate(normalizedValues)
    setImportedUnit({ ...importedUnit, rspo: -1 })
    setAlertModal({
      isOpen: null,
      isLogout: false,
      redirect: '',
    })
  }
  const curLocation = useLocation()
  const reachNavigate = useNavigate()

  const handleReturn = () => {
    if (alertModal.isLogout) {
      logout()
    } else {
      reachNavigate(`${alertModal.redirect}`)
    }
  }

  useEffect(() => {
    return globalHistory.listen(({ action, location }) => {
      if (
        alertModal.isOpen === false &&
        (action === 'POP' || action === 'PUSH')
      ) {
        setAlertModal({
          isOpen: true,
          isLogout: false,
          redirect: location.pathname,
        })
        reachNavigate(`${curLocation.pathname}`)
        navigate(`${curLocation.pathname}`, {
          state: {
            unit: currentUnit,
          },
        })
      }
    })
  }, [alertModal])
  const observer = useRef<IntersectionObserver>()
  const lastTableElement = useCallback(
    (element) => {
      if (observer.current) {
        observer.current.disconnect()
      }
      observer.current = new IntersectionObserver((entries) => {
        if (
          entries[0].isIntersecting &&
          ready &&
          usersState.meta.limit !== usersState.meta.total
        ) {
          const newLimit =
            usersState.meta.limit + 10 <= usersState.meta.total
              ? usersState.meta.limit + 10
              : usersState.meta.total
          setUsersState({
            ...usersState,
            meta: { ...usersState.meta, limit: newLimit },
          })
        }
      })
      if (element) {
        observer.current.observe(element)
      }
    },
    [usersState, ready]
  )

  return (
    <Layout
      modalLogout={
        alertModal.isOpen === false
          ? () =>
              setAlertModal({
                isOpen: true,
                isLogout: true,
                redirect: '',
              })
          : null
      }
    >
      <Helmet htmlAttributes={{ lang: 'pl-PL' }}>
        <title>Jednostka</title>
      </Helmet>
      <Heading title={`Nazwa jednostki - ${currentUnit?.name}`}>
        <Button
          link={null}
          onClick={() =>
            hasPermissions(p.diagnosticModule)
              ? navigate('/app/units')
              : history.go(-2)
          }
          label="Powrót"
          icon={closeIconRed}
          width="auto"
          color="white"
          altTag=""
          imgAriaHidden={true}
          srText=""
        />
      </Heading>
      <PanelWrapper onSubmit={handleSubmit}>
        <LeftPanel>
          <h1>Informacje o jednostce</h1>
          <LoaderWrapper ready={unitReady}>
            <PlaceHolder />
          </LoaderWrapper>
          <FormWrapper style={{ visibility: unitReady ? 'visible' : 'hidden' }}>
            <UnitForm
              onSubmit={handleSubmit}
              ready={!!currentUnit}
              importedUnit={importedUnit}
              initialValues={currentUnit}
              handleSave={handleSave}
            />
          </FormWrapper>
          <AddUser
            modalReady={addUserModalReady}
            handleModalStates={handleModalStates}
            unitId={normalisedUnitId}
            onClose={() => setAddUserModalReady(false)}
          ></AddUser>
          {!isEmptyObject(editUserModalReady) && (
            <EditUser
              modalReady={editUserModalReady}
              handleModalStates={handleModalStates}
              user={editUserModalReady}
              unitId={normalisedUnitId}
              onClose={() => setEditUserModalReady({})}
            ></EditUser>
          )}
        </LeftPanel>
        <RightPanel>
          <Wrap>
            <h2>Połączone osoby</h2>
            <Button
              width="12rem"
              link={null}
              marginLeft={true}
              icon={userIcon}
              label="Dodaj osobę"
              onClick={() => setAddUserModalReady(true)}
              color="white"
              altTag=""
              imgAriaHidden={true}
              srText=""
            />
          </Wrap>

          <Table
            infiniteScroll={false}
            data={users}
            getLoadingProps={() => ({
              'aria-hidden': true,
              role: 'none',
            })}
            getTbodyProps={() => ({
              style: {
                overflowY: 'scroll',
                maxHeight: '525px',
              },
            })}
            getTheadTrProps={() => ({
              style: {
                display: 'flex',
                alignItems: 'center',
              },
            })}
            columns={[
              {
                Header: 'Imię i nazwisko',
                id: 'fullname',
                sortable: false,
                accessor: ({ firstName, lastName }) =>
                  `${firstName} ${lastName}`,
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Rola',
                sortable: false,
                id: 'roles',
                accessor: ({ roles }) =>
                  roles.map((role) => `${rolesConstant[role.name]} `),
                getProps: () => ({
                  role: undefined,
                }),
              },

              {
                Header: 'Akcje',
                sortable: false,
                Cell: ({ original }) =>
                  original === users[users.length - 1] ? (
                    <ActionsGridWrap ref={lastTableElement}>
                      {isHigherLevel(
                        original.roles.map((role) => role.level)
                      ) || user.userId === original.id ? (
                        <>
                          <IconButton
                            type="button"
                            onClick={() => setEditUserModalReady(original)}
                          >
                            Edytuj
                            <Img alt="" src={editIcon} aria-hidden="true" />
                          </IconButton>
                          <IconButton
                            type="button"
                            onClick={() => handleDelete(original.id)}
                          >
                            Usuń
                            <Img alt="" src={deleteIcon} aria-hidden="true" />
                          </IconButton>
                        </>
                      ) : null}
                    </ActionsGridWrap>
                  ) : (
                    <ActionsGridWrap>
                      {isHigherLevel(
                        original.roles.map((role) => role.level)
                      ) || user.userId === original.id ? (
                        <>
                          <IconButton
                            type="button"
                            onClick={() => setEditUserModalReady(original)}
                          >
                            Edytuj
                            <Img alt="" src={editIcon} aria-hidden="true" />
                          </IconButton>
                          <IconButton
                            type="button"
                            onClick={() => handleDelete(original.id)}
                          >
                            Usuń
                            <Img alt="" src={deleteIcon} aria-hidden="true" />
                          </IconButton>
                        </>
                      ) : null}
                    </ActionsGridWrap>
                  ),
              },
            ]}
          />
        </RightPanel>
      </PanelWrapper>
      <AlertModal
        isOpen={alertModal.isOpen}
        isLogout={alertModal.isLogout}
        onReturn={() => handleReturn()}
        onClose={() =>
          setAlertModal({ isOpen: false, isLogout: false, redirect: '' })
        }
      />
    </Layout>
  )
}

export default decorate(EditUnit)

const PanelWrapper = styled.div`
  h2,
  h1 {
    font-size: 1.5em;
    font-weight: 400;
  }
  display: grid;
  @media (max-width: 1325px) {
    display: flex;
    flex-direction: column;
  }
  margin: 2em;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0px 2em;
  grid-template-areas: '. .';
  justify-content: space-between;
`
const Wrap = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const LeftPanel = styled.div`
  padding: 2em;
  border-radius: 6px;
  border: 1px solid #ddd;
`
const RightPanel = styled.div`
  min-width: 700px;
  padding: 2em;
  border-radius: 6px;
  border: 1px solid #ddd;
  @media (max-width: 1325px) {
    margin-top: 2em;
  }
`
const Img = styled.img`
  margin-bottom: 0;
  color: ${colors.dark_red};
`

const IconButton = styled.button`
  display: flex;
  margin-right: 25px;
  color: ${colors.dark_red};
  background: transparent;
  border: none;
  align-items: center;
  cursor: ${(props) => !props.disabled && `pointer;`};
  height: auto;
  border-radius: 5px;
  > * {
    padding: 0 0 0 3px;
  }
  ${Img} {
    opacity: ${(props) => props.disabled && `50%`};
  }
`
const ActionsGridWrap = styled.div`
  width: 70%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr;
  gap: 0px 0em;
  grid-template-areas: '. .';
  justify-content: space-between;
`
const FormWrapper = styled.div`
  margin-top: 5rem;
`

const LoaderWrapper = styled.div`
  position: relative;
  top: 35%;
  display: ${(props) => (props.ready ? `none` : 'block')};
`
